import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { twpApiURL } from '../api';
import { AccrualEmployeeStartInterval } from 'constants/accrualConstants';
import { accrualsFormats } from 'src/helpers/formatting';
import { fetchPolicies } from 'store/accrualsSlice';
import { hasValue, removeItem, sleep, deepEqual } from 'swipeclock-frontend';

// Helper for sleeping just enough to show some loading time
const transitionSleep = async () => await sleep(350);

export const fetchEmployees = createAsyncThunk(
  'accruals/fetchEmployees',
  async (getInactive, { getState, rejectWithValue }) => {
    const user = await getState().user;
    const api = twpApiURL(user);

    const employeeTransformer = ({ data: { Results } }) => {
      return Results?.reduce((list, item) => {
        const EmployeeId = item?.RecordNumber;
        const variables = item?.States?.[0].Variables ?? {};

        return [
          ...list,
          {
            fullName: item?.Name2,
            type: variables.EMPLOYEETYPE || variables.EmployeeType,
            departmentName: variables.DEPARTMENT ?? variables.Department,
            positionName: variables.HOME1 ?? variables.Home1,
            locationName: variables.LOCATION ?? variables.Location,
            EmployeeId: EmployeeId,
            active: !hasValue(item?.EndDate), // EndDate comes back as either a date string or a blank string, hasValue checks to see if values are "blank strings", and if so marks it as no value or false
            inactive: hasValue(item?.EndDate),
          },
        ];
      }, []);
    };

    // We also need to get payroll.

    try {
      const shouldGetInactive = getInactive || false;
      const response = await api.fetchEmployees({
        fetchInactiveEmployees: shouldGetInactive,
      });
      return employeeTransformer(response);
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

export const enrollEmployees = createAsyncThunk(
  'accruals/enrollEmployees',
  async (_, { getState, dispatch, rejectWithValue, fulfillWithValue }) => {
    const { policy } = await getState().accruals;
    const {
      enrollment: { enrolled },
    } = await getState().employees;
    const user = await getState().user;
    const api = twpApiURL(user);

    try {
      const employees = enrolled
        .filter((e) => e.selected)
        .map((e) => {
          let emp = removeItem('selected', e);
          return emp.StartInterval === AccrualEmployeeStartInterval.Hired
            ? removeItem('AccrualPolicyStartDate', emp)
            : emp;
        });

      await api.updateEmployeeEnrollment({
        policyID: policy?.ID,
        employees,
      });

      const count = employees.length;
      const s = count > 1 ? 's were' : ' was';
      await transitionSleep();

      dispatch(fetchPolicies());
      return fulfillWithValue({
        message: `Success!  ${count} employee${s} enrolled.`,
      });
    } catch (error) {
      return rejectWithValue({ error });
    }
  }
);

export const unenrollEmployees = createAsyncThunk(
  'accruals/unenrollEmployees',
  async (
    { selectedEmployees },
    { getState, dispatch, rejectWithValue, fulfillWithValue }
  ) => {
    const { policy } = await getState().accruals;
    const user = await getState().user;
    const api = twpApiURL(user);

    // TEMP call until unenroll many api call made
    try {
      await api.deleteEmployeeEnrollment({
        policyID: policy?.ID,
        employeeIDs: selectedEmployees.map((e) => e.EmployeeId),
      });

      const count = selectedEmployees.length;
      const s = count > 1 ? 's were' : ' was';

      await transitionSleep();

      dispatch(fetchPolicies());
      return fulfillWithValue({
        message: `Success!  ${count} employee${s} unenrolled.`,
      });
    } catch (error) {
      dispatch(fetchPolicies());
      return rejectWithValue({ error });
    }
  }
);

const slice = createSlice({
  name: 'accruals-employees',
  initialState: {
    isFetchingEmployees: true,
    isEnrollingEmployees: false,
    employees: [],
    enrollment: {
      roll: [],
      status: {},
      enrolled: [],
    },
  },
  reducers: {
    setEnrollment(state, action) {
      state.enrollment = action.payload.enrollment;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.isFetchingEmployees = true;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.isFetchingEmployees = false;
        state.employees = action.payload;
      })
      .addCase(fetchEmployees.rejected, (state) => {
        state.isFetchingEmployees = false;
      })
      .addCase(enrollEmployees.pending, (state) => {
        state.isEnrollingEmployees = true;
      })
      .addCase(enrollEmployees.fulfilled, (state) => {
        state.isEnrollingEmployees = false;
      })
      .addCase(enrollEmployees.rejected, (state) => {
        state.isEnrollingEmployees = false;
      })
      .addCase(unenrollEmployees.pending, (state) => {
        state.isEnrollingEmployees = true;
      })
      .addCase(unenrollEmployees.fulfilled, (state) => {
        state.isEnrollingEmployees = false;
      })
      .addCase(unenrollEmployees.rejected, (state) => {
        state.isEnrollingEmployees = false;
      });
  },
});

export const { setEnrollment } = slice.actions;

// Thunks!
// --------------------------------------------------------

// For checkbox
// Convient method for updating an employee's selection status
// selection status is needed for a user picks employees from a list ( the roll )
// and then is employee is selected assign a new accrual start interval to said employee
export function setEmployeeSelected(key, selected) {
  return async (dispatch, getState) => {
    const { enrollment: prev } = getState().employees; // Object that has roll, status, and enrolled

    // Create a new status for employee based on key (EmployeeID)
    // Update status for all of enrollment
    const nextStatus = {
      ...prev.status,
      [key]: { ...prev.status[key], selected },
    };
    const nextEnrollment = { ...prev, status: nextStatus };
    dispatch(setEnrollment({ enrollment: nextEnrollment }));
  };
}

export function setManyEmployeesSelected(employees, selected) {
  return async (dispatch) => {
    const keys = employees.map((e) => e.EmployeeId);
    keys.forEach((key) => dispatch(setEmployeeSelected(key, selected)));
  };
}

// Resets all status selection to false
export function resetAllEmpoyeeSelectionStatus() {
  return async (dispatch, getState) => {
    const { enrollment: prev } = getState().employees;
    const nextStatus = Object.keys(prev.status).reduce((newStatus, key) => {
      return { ...newStatus, [key]: { ...prev.status[key], selected: false } };
    }, {});
    const nextEnrollment = { ...prev, status: nextStatus };
    dispatch(setEnrollment({ enrollment: nextEnrollment }));
  };
}

// Updates enrollment's roll and status for enrollment and start interval
export function updateEmployeesEnrollment(employees, values) {
  return async (dispatch, getState) => {
    const { enrollment: prev } = getState().employees;
    const { enrollment: enrollmentValue, startDate } = values;

    const date = `${accrualsFormats.makeISODateFromWizardDate(startDate)}`;

    // First update status
    const newStatus = employees.reduce(
      (newStatusObj, employee) => {
        const id = employee.EmployeeId;
        return {
          ...newStatusObj,
          [id]: {
            ...newStatusObj[id],
            StartInterval: enrollmentValue,
            AccrualPolicyStartDate: date,
          },
        };
      },
      { ...prev.status } // init with "prev" or enrollment in state's status object
    );

    const newRoll = prev.roll.map((employee) =>
      createEnrollableEmployee(employee, newStatus)
    );

    dispatch(
      setEnrollment({
        enrollment: {
          roll: newRoll,
          status: newStatus,
          enrolled: filterStatusByWhoIsEnrolled(newStatus),
          hasRoll: hasValue(newRoll) && hasValue(newStatus),
          hasEnrollment: enrollmentStatus(newRoll),
        },
      })
    );
  };
}

// Builds out enrollment object to be used with Employee Wizard step and Editing of a policy
export function setupEmployeeEnrollment(employees, policy) {
  return async (dispatch) => {
    // Make dictionary of SelectedEmployees for easy 1:1 lookup with employee list
    const status = policy?.SelectedEmployees?.reduce((dic, e) => {
      return { ...dic, [e.EmployeeId]: e };
    }, {});

    const enrollment = employees.reduce(
      (e, employee) => {
        const { EmployeeId } = employee ?? {};
        const newEmployee = createEnrollableEmployee(employee, status);
        const newStatus = createSelectableEmployeeStatus(employee, status);
        return {
          roll: [...e.roll, newEmployee],
          status: { ...e.status, [EmployeeId]: newStatus },
        };
      },
      {
        roll: [],
        status: {},
      }
    );

    dispatch(
      setEnrollment({
        enrollment: {
          ...enrollment,
          enrolled: filterStatusByWhoIsEnrolled(enrollment.status),
          hasRoll: hasValue(enrollment.roll) && hasValue(enrollment.status),
          hasEnrollment: enrollmentStatus(enrollment.roll),
        },
      })
    );
  };
}

// Helper methods
// --------------------------------------------------------

// filter out only those enrolled
function filterStatusByWhoIsEnrolled(status) {
  return Object.values(status).filter(
    (s) => s.StartInterval !== AccrualEmployeeStartInterval.NotEnrolled
  );
}

// Creates new employee object that includes enrollment value
function createEnrollableEmployee(employee, status) {
  const { AccrualPolicyStartDate, StartInterval } =
    status?.[employee.EmployeeId] ?? {};
  return {
    ...employee,
    enrollment: StartInterval ?? AccrualEmployeeStartInterval.NotEnrolled,
    startInterval: AccrualPolicyStartDate,
    displayDate: accrualsFormats.formatDateForWizard(AccrualPolicyStartDate),
  };
}

// Creates new status object with selected value to keep track of being checked/selected
function createSelectableEmployeeStatus(employee, status) {
  return hasValue(status?.[employee.EmployeeId])
    ? { ...status[employee.EmployeeId], selected: false }
    : {
        EmployeeId: employee.EmployeeId,
        StartInterval: AccrualEmployeeStartInterval.NotEnrolled,
        // AccrualPolicyStartDate: '0001-01-01T00:00:00',
        selected: false,
      };
}

function enrollmentStatus(roll) {
  const enrolled = roll.filter(
    (e) => e.enrollment !== AccrualEmployeeStartInterval.NotEnrolled
  );
  return hasValue(enrolled);
}

// Selectors
// --------------------------------------------------------

export const employeesSelector = R.path(['employees', 'employees']);
export const enrollmentSelector = R.path(['employees', 'enrollment']);

export function useEmployeesSelector() {
  return useSelector(employeesSelector, deepEqual);
}

export function useEnrollmentSelector() {
  return useSelector(enrollmentSelector, deepEqual);
}

export function useIsFetchingEmpoyees() {
  return useSelector((state) => state.employees.isFetchingEmployees);
}

export function useIsEnrolingEmployees() {
  return useSelector((state) => state.employees.isEnrollingEmployees);
}

export function useEnrollmentCountSelector() {
  const enrollment = useSelector(enrollmentSelector, deepEqual);
  return enrollment?.enrolled.length || 0;
}

export default slice.reducer;
